import React, { useMemo, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { ISortedAvailableShift, ISortedShift } from '@/types/schedule';
import type { IWeek, IWeekDay } from '@/types/calendar';
import type { Hue } from '@/types/styles';

import { checkCollision, checkIllegalShift, classList, getDisplayBlocksForMultiDayView } from '@/helpers';
import { useAnimationSync, useAppSelector, useTimezone, useWeek } from '@/hooks';
import { colors, otherColors } from '@/styles/colors';
import { BaseShift } from '@/components/agent/my-schedule';
import { selectSelectedAvailableShifts } from '@/store/selectors/scheduleSelectors';

const shiftAvailableSelector = createSelector(selectSelectedAvailableShifts, selectedAvailableShifts => ({
  selectedAvailableShifts,
}));

interface IProps {
  block: ISortedShift<ISortedAvailableShift>;
  blockColor: Hue;
  label?: string;
  onClick?: () => void;
  day: IWeekDay;
  modal?: () => React.ReactElement;
  week: IWeek;
}

const ShiftAvailable = ({ block, blockColor, label, onClick, modal, day, week }: IProps): React.ReactElement => {
  const { selectedDays } = useWeek();
  const { timezoneName } = useTimezone();
  const shiftContainerRef = useRef<HTMLDivElement | null>(null);
  useAnimationSync({
    animatedElementRef: shiftContainerRef,
    animatedGroupClassName: 'available-shift-item',
    animationName: PULSE_ANIMATION_NAME,
  });

  const { selectedAvailableShifts } = useAppSelector(shiftAvailableSelector);
  const isCollision = useMemo<boolean>(
    () =>
      selectedAvailableShifts
        .map<ISortedShift<ISortedAvailableShift>>(item => {
          return {
            ...item,
            displayBlocks: getDisplayBlocksForMultiDayView(week, timezoneName, item.itemData, selectedDays, day),
          };
        })
        .some(
          item =>
            item.shiftId !== block.shiftId &&
            (checkCollision(
              { startBlock: block.originalStartBlock, endBlock: block.originalEndBlock },
              { startBlock: item.originalStartBlock, endBlock: item.originalEndBlock },
            ) ||
              checkIllegalShift(block, item)),
        ),
    [block, day, selectedAvailableShifts, selectedDays, timezoneName, week],
  );

  const isSelected = selectedAvailableShifts.some(item => item.shiftId === block.shiftId);

  return (
    <MemoizedShiftAvailableBlock
      className={classList(
        'available-shift-item',
        isCollision && 'available-shift-item-blocked',
        block.splitStart && 'available-shift-split-start',
        block.splitEnd && 'available-shift-split-end',
      )}
      block={block}
      blockColor={blockColor}
      isSelected={isSelected}
      onClick={onClick}
      modal={modal}
      day={day}
      ref={shiftContainerRef}
      label={label}
    />
  );
};

interface IShiftAvailableBlockProps {
  className?: string;
  block: ISortedShift<ISortedAvailableShift>;
  blockColor: Hue;
  isSelected?: boolean;
  onClick?: () => void;
  modal?: () => React.ReactElement;
  day: IWeekDay;
  label?: string;
}

const ShiftAvailableBlock = React.forwardRef<HTMLDivElement, IShiftAvailableBlockProps>(function ShiftAvailableBlock(
  props,
  forwardedRef,
) {
  return (
    <StyledBaseShift
      className={props.className}
      block={props.block}
      isDlsBlocked={props.block.isShiftDlsBlocked}
      blockColor={props.blockColor}
      isSelected={props.isSelected}
      onClick={props.onClick}
      modal={props.modal}
      day={props.day}
      ref={forwardedRef}
    >
      <label>{props.label}</label>
    </StyledBaseShift>
  );
});

const MemoizedShiftAvailableBlock = React.memo(ShiftAvailableBlock);

export { ShiftAvailable };

const pulseKeyframes = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 var(--animation-color-100);
    background-color: var(--animation-color-100);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 15px var(--animation-color-0);
    background-color: var(--animation-color-30);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 var(--animation-color-0); 
    background-color: var(--animation-color-100);
  }
`;

const PULSE_ANIMATION_NAME = pulseKeyframes.getName();

const StyledBaseShift = styled(BaseShift)`
  --animation-color-100: ${({ blockColor }) => colors[blockColor]['c200'] + 'FF'};
  --animation-color-30: ${({ blockColor }) => colors[blockColor]['c200'] + '21'};
  --animation-color-0: ${({ blockColor }) => colors[blockColor]['c200'] + '00'};

  align-items: flex-start;
  position: static;

  color: ${({ blockColor }) => colors[blockColor]['c500']};
  background-color: ${({ blockColor, isSelected }) => (isSelected ? colors[blockColor]['c200'] : '#fff')};
  border: 1px solid ${props => colors[props.blockColor]['c500']};
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.2s, box-shadow 0.2s, filter 0.2s;
  ${props =>
    props.isSelected &&
    css`
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
      transform: scale(1);
      animation: ${pulseKeyframes} 2s infinite;
    `};

  label {
    position: absolute;
    cursor: pointer;
  }

  &:hover {
    background-color: ${({ blockColor, isSelected }) => colors[blockColor][isSelected ? 'c200' : 'c50']};
    box-shadow: 0 0 5px ${props => colors[props.blockColor]['c500']};
    z-index: 101;
  }

  /* default styles for available shift */

  .hour,
  .title {
    display: none;
    transform: translate3d(0, 5px, 0);

    :not([data-row-length='1'] &) {
      padding: 0;
    }
  }

  .schedule-index {
    display: flex;
  }

  &.available-shift-item-blocked {
    pointer-events: none;
    opacity: 0.5;
    filter: blur(3px);
  }

  /* styles for split available shift */

  &.available-shift-split-start,
  &.available-shift-split-end {
    border: 0;

    .title,
    .hour {
      z-index: 2;
    }

    &:before,
    &:after {
      content: '';
      width: 100%;
      position: absolute;
      left: 0;
      background-color: inherit;
      pointer-events: none;
      border-radius: inherit;
    }

    &:before {
      height: 15px;
      z-index: 1;

      .current-day & {
        background-color: ${otherColors.calendar.currentDay};
      }

      .dls-day & {
        background-color: ${otherColors.calendar.dlsDay};
      }
    }

    &:after {
      height: 100%;
      top: 0;
    }
  }

  &.available-shift-split-start {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    &:before {
      bottom: 0;
      mask-image: linear-gradient(to bottom, transparent 0, #fff 15px);
    }

    &:after {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      border: 1px solid ${props => colors[props.blockColor]['c500']};
      border-bottom: 0;
    }
  }

  &.available-shift-split-end {
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    &:before {
      top: 0;
      mask-image: linear-gradient(to top, transparent 0, #fff 15px);
    }

    &:after {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      border: 1px solid ${props => colors[props.blockColor]['c500']};
      border-top: 0;
    }
  }
`;
